import { Injectable } from '@angular/core';
import { Http, QueryBus, Weaver, WeaverBase } from '@skylab-angular-shared/core';
import { deserializeInventoryDocumentBuyerModel, deserializeInventoryDocumentClientModel, documentDeliveryAddressFilled, SaleAdvanceRepositoryBase, SaleDocumentAdjustmentRepositoryBase, SaleDocumentRepositoryBase, serializeAddressModel, serializeDocumentExchangeRateModel, serializeInventoryDocumentBuyerModel, serializeInventoryDocumentClientModel } from '@skylab-shared/business';
import { HttpService, ObservableCollection, QueryBus as QueryBusBase, Repository, RepositoryBase, TaggedUrl, Tags } from '@skylab-shared/core';
import { AddressModel, CalculationMethod, ExchangeRateModel, InventoryDocumentItemModel, InventoryGetGoodsIssueDocumentDTO, InventoryGoodsIssueDocumentModel, InventoryUpdateGoodsIssueDocumentDTO, RelatedEntity, VatTableItemModel } from '@skylab-shared/dataModel';
import { getStartOfDate, pickBy } from '@skylab-shared/utils';
import { ProductRepository } from '../product/product.repo';
import { SaleAdvanceRepository } from '../sale/sale-advance.repo';
import { SaleDocumentAdjustmentRepository } from '../sale/sale-document-adjustment.repo';
import { SaleDocumentRepository } from '../sale/sale-document.repo';
import { InventoryGoodsIssueDocumentBO } from './inventory-goods-issue-document.bo';

@Repository({
    boType: InventoryGoodsIssueDocumentBO,
    apiController: TaggedUrl.join('inventory/goodsIssue', [Tags.Api]),
    dto: {
        get: InventoryGetGoodsIssueDocumentDTO,
        update: InventoryUpdateGoodsIssueDocumentDTO
    }
})
@Injectable({ providedIn: 'root' })
export class InventoryGoodsIssueDocumentRepository extends RepositoryBase<InventoryGoodsIssueDocumentBO, InventoryGoodsIssueDocumentModel, InventoryGetGoodsIssueDocumentDTO, InventoryUpdateGoodsIssueDocumentDTO> {

    public constructor(
        private readonly weaver: Weaver,
        private readonly queryBus: QueryBus,
        private readonly productRepository: ProductRepository,
        private readonly saleDocumentRepository: SaleDocumentRepository,
        private readonly saleDocumentAdjustmentRepository: SaleDocumentAdjustmentRepository,
        private readonly saleAdvanceRepository: SaleAdvanceRepository,
        http: Http
    ) {
        super(http as HttpService);
    }

    protected createBO(): InventoryGoodsIssueDocumentBO {
        return new InventoryGoodsIssueDocumentBO(this.weaver as WeaverBase, this.productRepository, this.queryBus as any as QueryBusBase, this.saleDocumentRepository as SaleDocumentRepositoryBase, this.saleDocumentAdjustmentRepository as SaleDocumentAdjustmentRepositoryBase, this.saleAdvanceRepository as SaleAdvanceRepositoryBase);
    }

    //#region Mapping
    protected async preparePlainObjectAsync(model: InventoryGoodsIssueDocumentModel): Promise<InventoryUpdateGoodsIssueDocumentDTO> {
        return {
            duplicatePreventionId: model.duplicatePreventionId,
            id: model.id,
            version: model.version,
            currencyId: model.currencyId,
            calculationMethod: model.calculationMethod,
            issuer: model.issuer,
            recipient: model.recipient,
            note: model.note,
            issueDate: getStartOfDate(model.issueDate),
            relatedCommercialDocuments: model.relatedCommercialDocuments.map(doc => ({
                id: doc.id,
                version: doc.version
            })) || [],
            numberingId: model.numberingId,
            goodsIssueDate: getStartOfDate(model.goodsIssueDate),
            grossValue: model.grossValue,
            communityTransactionId: model.communityTransactionId,
            deliveryAddress: model.deliveryAddress && documentDeliveryAddressFilled(model.deliveryAddress) ? serializeAddressModel(model.deliveryAddress) : null,
            wstoShipmentCountryId: model.wstoShipmentCountryId,
            wstoConsumerCountryId: model.wstoConsumerCountryId,
            wstoAdditionalInformation: model.wstoAdditionalInformation,
            netValue: model.netValue,
            transactionLocation: model.transactionLocation,
            documentNumber: model.documentNumber,
            exchangeRate: model.exchangeRate ? serializeDocumentExchangeRateModel(model.exchangeRate) : null,
            creationMethod: model.creationMethod,
            items: model.items.map(elm => ({
                discountValue: elm.discountValue,
                discountKind: elm.discountKind,
                no: elm.ordinalNumber,
                note: elm.note,
                productId: elm.productId,
                productName: elm.productName,
                pkwiu: elm.pkwiu,
                cnCode: elm.cnCode,
                symbol: elm.symbol,
                barCode: elm.barCode,
                relatedCommercialItemsIds: elm.relatedCommercialItemsIds || [],
                jpkVatProductGroup: elm.jpkVatGroup,
                quantity: elm.quantity,
                uniqueKey: elm.uniqueKey,
                unitId: elm.unitId,
                price: model.calculationMethod === CalculationMethod.Gross ? elm.price_grossBeforeDiscount : elm.price_netBeforeDiscount,
                vatRateId: elm.vatRateId
            })),
            inventoryId: model.inventoryId,
            kind: model.kind,
            client: serializeInventoryDocumentClientModel(model.client),
            buyer: serializeInventoryDocumentBuyerModel(model.buyer)
        };
    }

    protected async fromPlainObjectAsync(item: InventoryGetGoodsIssueDocumentDTO): Promise<InventoryGoodsIssueDocumentModel> {
        return new InventoryGoodsIssueDocumentModel(pickBy<InventoryGoodsIssueDocumentModel>({
            duplicatePreventionId: item.duplicatePreventionId,
            id: item.id,
            version: item.version,
            currencyId: item.currencyId,
            calculationMethod: item.calculationMethod,
            relatedCommercialDocuments: item.relatedCommercialDocuments.map(doc => new RelatedEntity(doc.id, doc.version)) || [],
            grossValue: item.grossValue,
            netValue: item.netValue,
            issuer: item.issuer,
            recipient: item.recipient,
            issueDate: item.issueDate,
            numberingId: item.numberingId,
            kind: item.kind,
            note: item.note,
            goodsIssueDate: item.goodsIssueDate,
            communityTransactionId: item.communityTransactionId,
            transactionLocation: item.transactionLocation,
            wstoShipmentCountryId: item.wstoShipmentCountryId,
            wstoConsumerCountryId: item.wstoConsumerCountryId,
            wstoAdditionalInformation: item.wstoAdditionalInformation,
            issuedByVatPayer: item.issuedByVatPayer,
            documentNumber: item.documentNumber,
            inventoryId: item.inventoryId,
            items: new ObservableCollection(item.items.map(documentItem => {
                return new InventoryDocumentItemModel(pickBy<Partial<InventoryDocumentItemModel>>({
                    ordinalNumber: documentItem.no,
                    discountKind: documentItem.discountKind,
                    discountValue: documentItem.discountValue,
                    note: documentItem.note,
                    price_grossAfterDiscount: documentItem.unitGrossPriceAfterDiscount,
                    price_grossBeforeDiscount: documentItem.unitGrossPrice,
                    price_netAfterDiscount: documentItem.unitNetPriceAfterDiscount,
                    price_netBeforeDiscount: documentItem.unitNetPrice,
                    productId: documentItem.productId,
                    productName: documentItem.productName,
                    pkwiu: documentItem.pkwiu,
                    relatedCommercialItemsIds: documentItem.relatedCommercialItemsIds || [],
                    cnCode: documentItem.cnCode,
                    symbol: documentItem.symbol,
                    barCode: documentItem.barCode,
                    id: documentItem.id,
                    jpkVatGroups: documentItem.jpkVatProductGroup ? [documentItem.jpkVatProductGroup] : [],
                    quantity: documentItem.quantity,
                    uniqueKey: documentItem.uniqueKey,
                    unitId: documentItem.unitId,
                    value_grossAfterDiscount: documentItem.grossValueAfterDiscount,
                    value_grossBeforeDiscount: documentItem.grossValue,
                    value_netAfterDiscount: documentItem.netValueAfterDiscount,
                    value_netBeforeDiscount: documentItem.netValue,
                    value_vatAfterDiscount: documentItem.vatValueAfterDiscount,
                    value_vatBeforeDiscount: documentItem.vatValue,
                    vatRateId: documentItem.vatRateId,
                    inventoryTotalCost: documentItem.inventoryTotalCost,
                    inventoryUnitCost: documentItem.inventoryUnitCost,
                }, val => val !== void 0));
            })),
            client: deserializeInventoryDocumentClientModel(item.client),
            buyer: deserializeInventoryDocumentBuyerModel(item.buyer),
            exchangeRate: item.exchangeRate ? new ExchangeRateModel(pickBy<ExchangeRateModel>({
                date: item.exchangeRate.date,
                manual: item.exchangeRate.manual,
                rate: item.exchangeRate.rate,
                tableNo: item.exchangeRate.tableNo,
                units: item.exchangeRate.units
            }, val => val !== void 0)) : null,
            vatTableItems: new ObservableCollection(item.vatTable.items.map(item => {
                return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                    grossValue: item.currencyGross ?? item.gross,
                    netValue: item.currencyNet ?? item.net,
                    vatRateId: item.vatRateId,
                    vatValue: item.currencyVat ?? item.vat,
                    cost: item.cost
                }, val => val !== void 0));
            })),
            vatTableItemsInBaseCurrency: item.exchangeRate ? new ObservableCollection(item.vatTable.items.map(item => {
                return new VatTableItemModel(pickBy<Partial<VatTableItemModel>>({
                    grossValue: item.gross,
                    netValue: item.net,
                    vatRateId: item.vatRateId,
                    vatValue: item.vat,
                    cost: item.cost
                }, val => val !== void 0));
            })) : new ObservableCollection(),
            deliveryAddress: item.deliveryAddress ? new AddressModel(pickBy<AddressModel>({
                line1: item.deliveryAddress.address,
                name: item.deliveryAddress.name,
                city: item.deliveryAddress.city,
                countryId: item.deliveryAddress.countryId,
                zipCode: item.deliveryAddress.zipCode
            }, val => val !== void 0)) : null
        }, val => val !== void 0));
    }
    //#endregion
}

